/**
 * Tiny synthesized sound kit — no audio files, just WebAudio oscillators and
 * shaped noise, so the whole thing costs nothing to download.
 *
 * Sound is ON by default (as on the reference). Browsers still refuse to play
 * audio before a user gesture, so `primeAudio` is called on the first pointer
 * or key event to wake the context; after that even hover ticks work.
 */

const STORAGE_KEY = 'hh-sfx-muted';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;

const readStored = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

let muted = typeof window !== 'undefined' ? readStored() : false;

const ensure = () => {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = muted ? 0 : 0.5;
    master.connect(ctx.destination);
  }
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
};

/** Wake the audio context on the first gesture so later hovers are audible. */
export const primeAudio = () => {
  ensure();
};

const noiseBuffer = (c: AudioContext, seconds: number) => {
  const buf = c.createBuffer(1, Math.max(1, (c.sampleRate * seconds) | 0), c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buf;
};

export const setSfxMuted = (m: boolean) => {
  muted = m;
  try {
    localStorage.setItem(STORAGE_KEY, m ? '1' : '0');
  } catch {
    /* private mode — the preference just won't persist */
  }
  const c = ensure();
  if (master && c) master.gain.setTargetAtTime(m ? 0 : 0.5, c.currentTime, 0.02);
};

export const isSfxMuted = () => muted;

/**
 * Hover: a barely-there felt tick. Pitch wanders a little each time so a run
 * of hovers sounds organic rather than like a machine.
 */
export const playHover = () => {
  const c = ensure();
  if (!c || !master || muted) return;
  const t = c.currentTime;

  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c, 0.022);
  const bp = c.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 3200 + Math.random() * 1400;
  bp.Q.value = 1.4;
  const g = c.createGain();
  g.gain.setValueAtTime(0.045, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.022);
  src.connect(bp).connect(g).connect(master);
  src.start(t);

  const o = c.createOscillator();
  const og = c.createGain();
  o.type = 'sine';
  o.frequency.setValueAtTime(880 + Math.random() * 220, t);
  og.gain.setValueAtTime(0.0001, t);
  og.gain.exponentialRampToValueAtTime(0.035, t + 0.004);
  og.gain.exponentialRampToValueAtTime(0.0001, t + 0.045);
  o.connect(og).connect(master);
  o.start(t);
  o.stop(t + 0.06);
};

/** Click: a felt thock with a whisper of noise. For buttons and links. */
export const playClick = () => {
  const c = ensure();
  if (!c || !master || muted) return;
  const t = c.currentTime;

  const o = c.createOscillator();
  const g = c.createGain();
  o.type = 'sine';
  o.frequency.setValueAtTime(210, t);
  o.frequency.exponentialRampToValueAtTime(140, t + 0.05);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.22, t + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.07);
  o.connect(g).connect(master);
  o.start(t);
  o.stop(t + 0.09);

  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c, 0.02);
  const hp = c.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 5200;
  const ng = c.createGain();
  ng.gain.setValueAtTime(0.05, t);
  ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.018);
  src.connect(hp).connect(ng).connect(master);
  src.start(t);
};

/** Airy rising swell for something opening (menu, theatre). */
export const playOpen = () => {
  const c = ensure();
  if (!c || !master || muted) return;
  const t = c.currentTime;
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c, 0.4);
  const bp = c.createBiquadFilter();
  bp.type = 'bandpass';
  bp.Q.value = 0.8;
  bp.frequency.setValueAtTime(500, t);
  bp.frequency.exponentialRampToValueAtTime(3400, t + 0.32);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.14, t + 0.1);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.38);
  src.connect(bp).connect(g).connect(master);
  src.start(t);

  const o = c.createOscillator();
  const og = c.createGain();
  o.type = 'sine';
  o.frequency.setValueAtTime(150, t);
  o.frequency.exponentialRampToValueAtTime(240, t + 0.3);
  og.gain.setValueAtTime(0.0001, t);
  og.gain.exponentialRampToValueAtTime(0.1, t + 0.08);
  og.gain.exponentialRampToValueAtTime(0.0001, t + 0.34);
  o.connect(og).connect(master);
  o.start(t);
  o.stop(t + 0.4);
};

/** The same gesture falling shut. */
export const playClose = () => {
  const c = ensure();
  if (!c || !master || muted) return;
  const t = c.currentTime;
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c, 0.28);
  const bp = c.createBiquadFilter();
  bp.type = 'bandpass';
  bp.Q.value = 0.8;
  bp.frequency.setValueAtTime(2800, t);
  bp.frequency.exponentialRampToValueAtTime(420, t + 0.22);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.12, t + 0.05);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.26);
  src.connect(bp).connect(g).connect(master);
  src.start(t);
};
